import { useEffect } from "react";
import { matchPath, useLocation, type Params } from "react-router-dom";

const BRAND_NAME = "Grid2";
const LANDING_PAGE_TITLE = "Grid2 — Workflow and Data Operations Platform";

type RouteTitleContext = {
  params: Readonly<Params<string>>;
  searchParams: URLSearchParams;
};

type RouteTitleRule = {
  path: string;
  title: string | ((context: RouteTitleContext) => string);
};

function pageTitle(page: string): string {
  return `${BRAND_NAME} — ${page}`;
}

function referencedPageTitle(page: string, reference: string | undefined): string {
  return pageTitle(reference ? `${page} · ${reference}` : page);
}

const routeTitleRules: RouteTitleRule[] = [
  { path: "/", title: LANDING_PAGE_TITLE },
  { path: "/login", title: "Sign in" },
  { path: "/auth/bootstrap", title: "Preparing Your Account" },
  { path: "/onboarding", title: "Organization setup" },
  { path: "/connectors/intuit/connect", title: "Connect QuickBooks Online" },
  { path: "/connectors/intuit/disconnected", title: "QuickBooks Online disconnected" },
  { path: "/connectors/quickbooks-time/connect", title: "Connect QuickBooks Time" },
  {
    path: "/forms/public/:formId",
    title: ({ params }) => referencedPageTitle("Public Form", params.formId),
  },
  {
    path: "/forms/:formId/view",
    title: ({ params, searchParams }) => {
      const tableId = searchParams.get("tableId");
      const rowId = searchParams.get("rowId");
      const references = [params.formId, tableId, rowId].filter(Boolean).join(" · ");
      return referencedPageTitle("Action Form", references);
    },
  },
  { path: "/dashboard", title: "Dashboard" },
  {
    path: "/flow",
    title: ({ searchParams }) =>
      searchParams.has("monitor")
        ? referencedPageTitle("Pipeline Run", searchParams.get("monitor") ?? undefined)
        : pageTitle("Pipelines"),
  },
  { path: "/flow/dashboard", title: "Flow Dashboard" },
  { path: "/flow/schedule", title: "Flow Schedule" },
  { path: "/forms", title: "Forms" },
  {
    path: "/forms/:formId/edit",
    title: ({ params }) => referencedPageTitle("Edit Form", params.formId),
  },
  { path: "/action-tables", title: "Action Tables" },
  { path: "/action-tables/new", title: "New Action Table" },
  {
    path: "/action-tables/:tableId/edit",
    title: ({ params }) => referencedPageTitle("Edit Action Table", params.tableId),
  },
  {
    path: "/action-tables/:tableId",
    title: ({ params }) => referencedPageTitle("Action Table", params.tableId),
  },
  { path: "/settings", title: "Settings" },
  { path: "/teams", title: "Teams" },
  {
    path: "/teams/:teamId",
    title: ({ params }) => referencedPageTitle("Team", params.teamId),
  },
  { path: "/billing", title: "Billing" },
  { path: "/deployments", title: "Deployments" },
  { path: "/organization-admin", title: "Organization Administration" },
  { path: "/templates", title: "Templates" },
  { path: "/templates/xero-recipe", title: "Xero Integration Recipe" },
  {
    path: "/templates/provisions/:provisionId",
    title: ({ params }) => referencedPageTitle("Template Provision", params.provisionId),
  },
  {
    path: "/templates/:templateId",
    title: ({ params }) => referencedPageTitle("Template", params.templateId),
  },
  { path: "/admin/login", title: "Admin Sign in" },
  { path: "/admin", title: "Admin Dashboard" },
  { path: "/admin/customers", title: "Admin Customers" },
  {
    path: "/admin/customers/:tenantId",
    title: ({ params }) => referencedPageTitle("Admin Customer", params.tenantId),
  },
  { path: "/admin/tenants", title: "Legacy Admin Organizations" },
  {
    path: "/admin/tenants/:tenantId",
    title: ({ params }) => referencedPageTitle("Legacy Admin Organization", params.tenantId),
  },
  { path: "/admin/health", title: "System Health" },
  { path: "/admin/administrators", title: "Platform Administrators" },
  { path: "/admin/billing", title: "Billing Administration" },
  { path: "/admin/billing/products", title: "Product Catalog Administration" },
  { path: "/admin/billing/pricing", title: "Pricing Administration" },
  { path: "/admin/billing/subscriptions", title: "Subscription Administration" },
  { path: "/admin/billing/trials", title: "Trial Administration" },
];

export function getDocumentTitle(pathname: string, search = ""): string {
  const searchParams = new URLSearchParams(search);

  for (const rule of routeTitleRules) {
    const match = matchPath({ path: rule.path, end: true }, pathname);
    if (!match) continue;

    const title =
      typeof rule.title === "function"
        ? rule.title({ params: match.params, searchParams })
        : rule.title;

    return title.includes(BRAND_NAME) ? title : pageTitle(title);
  }

  return referencedPageTitle("Page not found", pathname || "/");
}

export function DocumentTitle() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    document.title = getDocumentTitle(pathname, search);
  }, [pathname, search]);

  return null;
}
